import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const FloorForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [sites, setSites] = useState([]);
    const [allWings, setAllWings] = useState([]);
    const [wings, setWings] = useState([]);
    const [formData, setFormData] = useState({
        floorId: null,
        siteId: null,
        wingId: null,
        floorNumber: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editItem = location.state?.item;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Sites for dropdown
                const siteRes = await apiServices({
                    method: 'GET',
                    url: '/ResidentialSite/GetAllResidentialSites'
                });

                if (siteRes.success || siteRes.isSuccess || siteRes.IsSuccess || siteRes) {
                    let siteData = [];
                    let actualData = siteRes.data || siteRes;
                    if (Array.isArray(actualData)) {
                        siteData = actualData;
                    } else if (actualData.data && Array.isArray(actualData.data)) {
                        siteData = actualData.data;
                    }

                    const formattedSites = siteData
                        .filter(s => s.IsActive === 'Active' || s.isActive === 'Active')
                        .map(s => ({
                            label: s.SiteName || s.siteName,
                            value: s.SiteId || s.siteId
                        }));
                    setSites(formattedSites);
                }

                // Fetch all wings to filter locally when site is selected
                const wingRes = await apiServices({
                    method: 'GET',
                    url: '/ResWingMaster/GetAllWingMaster'
                });

                if (wingRes.success || wingRes.isSuccess || wingRes.IsSuccess || wingRes) {
                    let wingData = [];
                    let actualData = wingRes.data || wingRes;
                    if (Array.isArray(actualData)) {
                        wingData = actualData;
                    } else if (actualData.data && Array.isArray(actualData.data)) {
                        wingData = actualData.data;
                    }
                    setAllWings(wingData);
                }

                if (isEdit && editItem) {
                    setFormData({
                        floorId: editItem.floorId || editItem.FloorId || null,
                        siteId: editItem.siteId || editItem.SiteId || null,
                        wingId: editItem.wingId || editItem.WingId || null,
                        floorNumber: editItem.floorName || editItem.floorNumber || editItem.FloorNumber || '',
                        isActive: editItem.isActive || editItem.IsActive || (editItem.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active'
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            }
        };

        fetchMasterData();
    }, [isEdit, editItem]);

    // Update wings dropdown when site changes
    useEffect(() => {
        if (!formData.siteId) {
            setWings([]);
            return;
        }

        const filteredWings = allWings
            .filter(w => (w.siteId || w.SiteId) === formData.siteId && (w.IsActive === 'Active' || w.isActive === 'Active' || w.isActiveId === 1))
            .map(w => ({
                label: w.WingName || w.wingName,
                value: w.WingId || w.wingId || w.Id || w.id
            }));
        setWings(filteredWings);

        // Auto-select if only 1 wing
        if (filteredWings.length === 1 && !formData.wingId) {
            setFormData(prev => ({ ...prev, wingId: filteredWings[0].value }));
        }
    }, [formData.siteId, allWings]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'siteId') {
                newData.wingId = null; // reset wing if site changes
            }
            return newData;
        });
    };

    const validateForm = () => {
        if (!formData.siteId) return toast.warning("Site is required"), false;
        if (!formData.wingId) return toast.warning("Wing is required"), false;
        if (!formData.floorNumber || !formData.floorNumber.toString().trim()) return toast.warning("Floor Number/Name is required"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            floorId: formData.floorId || 0,
            siteId: formData.siteId,
            wingId: formData.wingId,
            floorName: formData.floorNumber.toString(),
            createdBy: 'sys',
            isActiveId: formData.isActive === 'Active' ? 1 : 0,
            status: formData.isActive
        };

        try {
            const url = isEdit ? `/ResFloorMaster/putFloor` : `/ResFloorMaster/postFloor`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success || response.isSuccess || response.IsSuccess || response) {
                toast.success(`Residential Floor ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/master-management/floor');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} floor.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const selectedSite = sites.find(s => s.value === formData.siteId) || null;
    const selectedWing = wings.find(w => w.value === formData.wingId) || null;

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Residential Floor`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} residential floor.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect
                    label={<>Site Name <span className="text-red-500">*</span></>}
                    options={sites}
                    value={selectedSite}
                    onChange={(opt) => handleSelectChange('siteId', opt)}
                />

                <CustomSelect
                    label={<>Wing Name <span className="text-red-500">*</span></>}
                    options={wings}
                    value={selectedWing}
                    onChange={(opt) => handleSelectChange('wingId', opt)}
                    disabled={!formData.siteId}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="floorNumber">
                        Floor <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors"
                        type="text"
                        id="floorNumber"
                        value={formData.floorNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., 5th Floor"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/master-management/floor")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default FloorForm;
